import {
  BriefcaseMedicalIcon,
  RepeatIcon,
  PhoneIcon,
  BugIcon
} from "lucide-react";
import { useSelector } from "react-redux";
import { Skeleton } from "../../components/ui/skeleton";
import { convertE164ToReadable } from "../../utils/utils";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../components/ui/select";
import {
  PlanOfCareDetails,
  usePlanOfCareDetails
} from "../../hooks/usePlanOfCareDetails";
import { useEffect } from "react";
import { usePlanOfCareStore } from "../../stores/planOfCareStore";
import { Progress } from "../../components/ui/progress";
import { parseAsBoolean, parseAsInteger, useQueryState } from "nuqs";
import { RootState } from "../../store";
import { Toggle } from "../../components/ui/toggle";

export function DetailsSection({ uniqueId }: { uniqueId: string | undefined }) {
  const { totalWeeks, debugMode, setDebugMode } = usePlanOfCareStore();
  const [week, setWeek] = useQueryState("week", parseAsInteger);
  const [poc, setPoc] = useQueryState("poc", parseAsInteger);
  // Lets a debug view be linked directly, e.g. ?debug=true — only takes
  // effect for a logged-in clinic user, same as the manual toggle below.
  const [debugParam] = useQueryState("debug", parseAsBoolean);
  // A clinic user with an active dashboard session (same browser) is
  // authenticated app-wide, even on this public patient-facing page.
  const clinicUser = useSelector((state: RootState) => state.auth.user);

  useEffect(() => {
    if (debugParam && clinicUser?.token) {
      setDebugMode(true);
    }
  }, [debugParam, clinicUser?.token, setDebugMode]);

  const { data, isLoading, error } = usePlanOfCareDetails({
    uniqueId,
    enabled: Boolean(uniqueId)
  });

  return (
    <div className="h-full flex flex-col xl:w-96 w-full">
      <div className="px-6 pb-6 pt-8">
        <div className="flex items-start justify-between gap-2">
          {isLoading || error ? (
            <Skeleton className="h-6 w-full" />
          ) : (
            <p className="text-base font-medium text-muted-foreground">
              {(data as PlanOfCareDetails).organization.name}
            </p>
          )}
          {clinicUser?.token && (
            <Toggle
              size="sm"
              variant="outline"
              pressed={debugMode}
              onPressedChange={setDebugMode}
              aria-label="Toggle debug view"
              className="shrink-0"
            >
              <BugIcon className="size-4" />
              Debug
            </Toggle>
          )}
        </div>
        {isLoading || error ? (
          <Skeleton className="h-[34px] w-full mt-1" />
        ) : (
          <>
            <div className="flex items-end justify-between gap-8 mb-0.5">
              <div className="flex items-end gap-2">
                <h1 className="header-2">Week {week || 1}</h1>
                <p className="text-sm font-medium text-muted-foreground mb-1">
                  of {totalWeeks}
                </p>
              </div>
            </div>
            <Progress
              value={((week || 1) / totalWeeks) * 100}
              className="h-1"
            />
          </>
        )}
        <div className="flex flex-col gap-2 mt-6 text-muted-foreground text-sm font-medium">
          <div className="flex items-center gap-2">
            <BriefcaseMedicalIcon aria-label="Case Study" className="size-4" />
            {isLoading || error ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              poc !== null && poc !== undefined && (data as PlanOfCareDetails).planOfCares.length > 1 ? (
                <Select value={(data as PlanOfCareDetails).planOfCares[poc].caseStudyId?.toString() || ""} onValueChange={(value) => {
                  const planOfCareIndex = (data as PlanOfCareDetails).planOfCares.findIndex((planOfCare) => planOfCare.caseStudyId?.toString() === value);
                  if (planOfCareIndex !== -1) {
                    setWeek(null);
                    setPoc(planOfCareIndex);
                  }
                }}>
                  <SelectTrigger aria-label="Select Case Study" className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {(data as PlanOfCareDetails).planOfCares.map((planOfCare) => (
                      <SelectItem key={planOfCare.caseStudyId} value={planOfCare.caseStudyId?.toString() || ""}>
                        {planOfCare.caseStudyName}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : poc !== null && poc !== undefined ? (
              <p>{(data as PlanOfCareDetails).planOfCares[poc].caseStudyName}</p>
              ) : null
            )}
          </div>
          {!isLoading && !error && poc !== null && poc !== undefined && (data as PlanOfCareDetails).planOfCares[poc].frequency && (
            <div className="flex items-center gap-2">
              <RepeatIcon aria-label="Frequency" className="size-4" />
              <p>{(data as PlanOfCareDetails).planOfCares[poc].frequency}x per week</p>
            </div>
          )}
          <div className="flex items-center gap-2">
            <PhoneIcon aria-label="Phone Number" className="size-4" />
            {isLoading || error ? (
              <Skeleton className="h-5 w-32" />
            ) : (
              <p>
                {convertE164ToReadable(
                  (data as PlanOfCareDetails).organization.phoneNumber || ""
                )}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
